import { reactive } from 'vue';
import store from '../store/prenotazione';
import { isLoggedStore } from '../store/logged';
import customerAPI from './customerAPI';
import partnerAPI from './partnerAPI';
import bookingAPI from './bookingAPI';

// -----------------------------------------------------------------------------
//  PREPARAZIONE DI UNA NUOVA PRENOTAZIONE
//
//  I form di prenotazione (Pickup, Transfer) non caricano i propri clienti e
//  le proprie sedi: li leggono già pronti da `store/prenotazione`. Questo file
//  li prepara, qualunque sia la pagina da cui si apre il form.
//
//  ⚠️ VINCOLO DI ORDINE
//  --------------------
//  I form catturano l'array UNA VOLTA SOLA, al proprio `setup()`:
//
//      const customersId = store.customersCascader;
//
//  Se allo `setup` del form il campo vale `null`, quel `null` se lo tiene per
//  sempre: riassegnare `store.customersCascader` più tardi non lo raggiunge.
//  Per questo gli array vengono creati e messi nello store SUBITO, in modo
//  sincrono, e le chiamate di rete li RIEMPIONO per riferimento.
// -----------------------------------------------------------------------------

/**
 * Stato del caricamento, per la pagina che lo vuole mostrare: una rete caduta
 * e un partner senza clienti si distinguono.
 */
export const statoPrenotazione = reactive({
  caricamento: false,
  errore: null,
  clientiTroncati: false,
});

/**
 * Riporta lo store ai valori iniziali, compresi `customerSelectedObj` e
 * `heaquarterSelectedObj`, che i form leggono.
 */
function azzeraStore() {
  // ⚠️ `customers`, `customersCascader`, `headquarters` e `headquartersCascader`
  // NON si azzerano qui: li tratta `svuotaOCrea`, che li vuota SENZA cambiarne
  // l'identità. Con il pulsante «Riprova» il form è già montato e ha già
  // catturato l'array: sostituirlo lo lascerebbe a guardare quello vecchio.
  store.customerSelected = null;
  store.customerSelectedObj = {};

  store.heaquarterSelected = null;
  store.heaquarterSelectedObj = {};

  store.cars = [];
  store.carsCascader = [];
  store.carSelected = null;

  store.originPointAddress = '';
  store.originPointStreetNumber = '';
  store.originPointCity = '';
  store.originPointCap = '';
  store.originPointProvince = '';

  store.destinationPointAddress = '';
  store.destinationPointStreetNumber = '';
  store.destinationPointCity = '';
  store.destinationPointCap = '';
  store.destinationPointProvince = '';

  store.rangeKmGonePickup = 0;
  store.rangeCostGonePickup = 0;
  store.rangeKmReturnPickup = 0;
  store.rangeCostReturnPickup = 0;

  store.rangeKmGoneEscort = 0;
  store.rangeCostGoneEscort = 0;
  store.rangeKmReturnEscort = 0;
  store.rangeCostReturnEscort = 0;
}

/**
 * Restituisce l'array dello store per `chiave`, vuoto e PRONTO a essere
 * riempito, conservandone l'identita' se gia' esiste.
 *
 * ⚠️ `attuale.length = 0` invece di `store[chiave] = []`. I form catturano
 * l'array al proprio `setup()` e non lo rileggono mai piu': svuotarlo sul
 * posto li raggiunge, sostituirlo no. E' la differenza fra un «Riprova» che
 * funziona e uno che sembra funzionare.
 */
function svuotaOCrea(chiave) {
  const attuale = store[chiave];
  if (Array.isArray(attuale)) {
    attuale.length = 0;
    return attuale;
  }
  store[chiave] = [];
  // ⚠️ Si RILEGGE dallo store invece di restituire l'array appena creato:
  // `store` è `reactive()`, e solo scrivendo attraverso il proxy Vue si accorge
  // della modifica e ridisegna la tendina.
  return store[chiave];
}

/** L'etichetta con cui il cliente compare nella tendina. */
function etichettaCliente(c) {
  return `${c.name} ${c.surname}, ${c.mail}`;
}

/** L'etichetta con cui la sede compare nella tendina. */
function etichettaSede(h) {
  return `${h.address} ${h.street_number} ${h.city}`;
}

async function caricaClienti(idImpiegato, clienti, clientiCascader) {
  const rispostaPartner = await partnerAPI.getPartnerByEmployee(idImpiegato);
  const partner = rispostaPartner?.data?.[0]?.headquarters?.[0]?.partner;

  // Un impiegato non ancora associato a una sede non ha `headquarters[0]`.
  if (!partner?.id) {
    throw new Error(
      'Il tuo utente non risulta associato a nessuna sede: non posso caricare i clienti.'
    );
  }

  const { rows, truncated } = await customerAPI.getCustomersByPartnerPaged(partner.id);

  // `is_alive` distingue il cliente attivo da quello archiviato.
  rows.forEach((cliente) => {
    if (cliente.is_alive === true) {
      clienti.push(cliente);
      clientiCascader.push({ value: cliente.id, label: etichettaCliente(cliente) });
    }
  });

  return truncated;
}

async function caricaSedi(idImpiegato, sedi, sediCascader) {
  const risposta = await bookingAPI.getHeadquartersByEmployee(idImpiegato);
  const elenco = risposta?.data?.[0]?.headquarters;

  if (!Array.isArray(elenco)) {
    throw new Error(
      'Il tuo utente non risulta associato a nessuna sede: non posso caricare le sedi.'
    );
  }

  elenco.forEach((sede) => {
    sedi.push(sede);
    sediCascader.push({ value: sede.id, label: etichettaSede(sede) });
  });
}

/**
 * Traduce l'errore in una frase che dica all'operatore cosa è successo: il
 * server irraggiungibile si riprova, la sessione scaduta si rifà il login, il
 * resto si segnala.
 */
function messaggioErrore(err) {
  // Gli errori che lanciamo noi (utente senza sede) hanno già la frase giusta.
  if (err?.message && !err.response && !err.request) return err.message;

  const stato = err?.response?.status;
  if (stato === 401 || stato === 403) {
    return 'La sessione è scaduta: esci e rientra per caricare i clienti.';
  }
  if (!err?.response) {
    return 'Non riesco a raggiungere il server: clienti e sedi non sono stati caricati.';
  }
  return 'Non sono riuscito a caricare clienti e sedi.';
}

/**
 * Azzera lo store e carica clienti e sedi per la prenotazione che si sta per
 * creare.
 *
 * Gli array finiscono nello store PRIMA di qualunque `await` (vedi il vincolo
 * di ordine in cima): chi monta un form subito dopo trova il contenitore
 * giusto e lo vede riempirsi.
 *
 * @returns {Promise<void>} si risolve a caricamento finito; non rilancia, lo
 *   esito sta in `statoPrenotazione.errore`.
 */
export async function preparaNuovaPrenotazione() {
  azzeraStore();

  // Sincrono, prima di qualunque `await`: chi monta un form subito dopo trova
  // il contenitore giusto e lo vede riempirsi. Vedi il vincolo di ordine in
  // cima al file.
  const clienti = svuotaOCrea('customers');
  const clientiCascader = svuotaOCrea('customersCascader');
  const sedi = svuotaOCrea('headquarters');
  const sediCascader = svuotaOCrea('headquartersCascader');

  statoPrenotazione.caricamento = true;
  statoPrenotazione.errore = null;
  statoPrenotazione.clientiTroncati = false;

  const storeLogin = isLoggedStore();

  try {
    // In parallelo: sono due letture indipendenti, e in serie raddoppiavano
    // l'attesa davanti a un form che senza di loro non si puo' compilare.
    const [troncati] = await Promise.all([
      caricaClienti(storeLogin.id, clienti, clientiCascader),
      caricaSedi(storeLogin.id, sedi, sediCascader),
    ]);
    statoPrenotazione.clientiTroncati = troncati;
  } catch (err) {
    statoPrenotazione.errore = messaggioErrore(err);
  } finally {
    statoPrenotazione.caricamento = false;
  }
}

export default { preparaNuovaPrenotazione, statoPrenotazione };
